(function () {
  'use strict';

  var CONTROLLER_SELECTOR = '[data-wk-infinite-collection]';
  var controllerMap = new WeakMap();

  function InfiniteCollection(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.productListId = root.dataset.productListId;
    this.isLoading = false;
    this.abortController = null;
    this.observer = null;
    this.onLoadMoreClick = this.loadNextPage.bind(this);

    this.refreshElements();
    this.bind();
  }

  InfiniteCollection.prototype.refreshElements = function () {
    this.productList = this.root.querySelector('#' + CSS.escape(this.productListId));
    this.paginationContainer = this.root.querySelector('[data-wk-pagination]');
    this.loadMoreButton = this.root.querySelector('[data-wk-load-more]');
    this.status = this.root.querySelector('[data-wk-load-status]');
    this.sentinel = this.root.querySelector('[data-wk-load-sentinel]');
    this.nextLink = this.paginationContainer
      ? this.paginationContainer.querySelector('.pagination a[rel="next"]')
      : null;
  };

  InfiniteCollection.prototype.bind = function () {
    if (!this.productList || !this.paginationContainer || !this.loadMoreButton || !this.sentinel) return;

    this.root.dataset.wkInfiniteActive = 'true';
    this.loadMoreButton.removeEventListener('click', this.onLoadMoreClick);
    this.loadMoreButton.addEventListener('click', this.onLoadMoreClick);

    this.updateControls();
    this.observeSentinel();
  };

  InfiniteCollection.prototype.observeSentinel = function () {
    if (this.observer) this.observer.disconnect();
    if (!('IntersectionObserver' in window) || !this.sentinel || !this.nextLink) return;

    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          self.loadNextPage();
          break;
        }
      }
    }, {
      rootMargin: '800px 0px',
      threshold: 0
    });

    this.observer.observe(this.sentinel);
  };

  InfiniteCollection.prototype.setStatus = function (message) {
    if (this.status) this.status.textContent = message || '';
  };

  InfiniteCollection.prototype.updateControls = function () {
    var hasNextPage = Boolean(this.nextLink);

    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = !hasNextPage;
      this.loadMoreButton.disabled = this.isLoading;
      this.loadMoreButton.textContent = this.isLoading ? 'Loading…' : 'Load more';
    }

    if (this.sentinel) this.sentinel.hidden = !hasNextPage;

    if (!hasNextPage) {
      //this.setStatus('All products loaded');
      if (this.observer) this.observer.disconnect();
    }
  };

  InfiniteCollection.prototype.buildRequestUrl = function (nextUrl) {
    var url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  };

  InfiniteCollection.prototype.loadNextPage = async function () {
    if (this.isLoading || !this.nextLink || !this.productList) return;

    this.isLoading = true;
    this.setStatus('Loading more products…');
    this.updateControls();

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      var response = await fetch(this.buildRequestUrl(this.nextLink.href), {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error('Collection page request failed with status ' + response.status);

      var html = await response.text();
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var incomingRoot = parsed.querySelector(CONTROLLER_SELECTOR);
      var incomingList = incomingRoot
        ? incomingRoot.querySelector('#' + CSS.escape(this.productListId))
        : parsed.querySelector('#' + CSS.escape(this.productListId));
      var incomingPagination = incomingRoot
        ? incomingRoot.querySelector('[data-wk-pagination]')
        : parsed.querySelector('[data-wk-pagination]');

      if (!incomingList || !incomingPagination) {
        throw new Error('The next collection page did not contain the expected product grid.');
      }

      var items = Array.prototype.slice.call(
        incomingList.querySelectorAll(':scope > [data-wk-collection-item]')
      );

      if (!items.length) {
        this.nextLink = null;
        this.setStatus('All products loaded');
        return;
      }

      var fragment = document.createDocumentFragment();
      for (var i = 0; i < items.length; i++) fragment.appendChild(items[i]);
      this.productList.appendChild(fragment);

      this.paginationContainer.innerHTML = incomingPagination.innerHTML;
      this.refreshElements();
      this.root.dataset.wkInfiniteActive = 'true';

      document.dispatchEvent(new CustomEvent('wk:collection:items-appended', {
        detail: {
          sectionId: this.sectionId,
          productList: this.productList,
          itemCount: items.length
        }
      }));

      var productCount = items.filter(function (item) {
        return item.hasAttribute('data-wk-product-item');
      }).length;

      this.setStatus(productCount + (productCount === 1 ? ' product loaded' : ' products loaded'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('WK infinite collection failed:', error);
        this.setStatus('Could not load more products. Please try again.');
      }
    } finally {
      this.isLoading = false;
      this.refreshElements();
      this.root.dataset.wkInfiniteActive = 'true';
      this.updateControls();
      this.loadMoreButton.removeEventListener('click', this.onLoadMoreClick);
      this.loadMoreButton.addEventListener('click', this.onLoadMoreClick);
      this.observeSentinel();
    }
  };

  InfiniteCollection.prototype.destroy = function () {
    if (this.observer) this.observer.disconnect();
    if (this.abortController) this.abortController.abort();
    if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.onLoadMoreClick);
  };

  function initialiseWithin(scope) {
    var roots = [];

    if (scope.matches && scope.matches(CONTROLLER_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll(CONTROLLER_SELECTOR)));
    }

    roots.forEach(function (root) {
      var existing = controllerMap.get(root);
      if (existing) {
        existing.refreshElements();
        existing.bind();
        return;
      }

      controllerMap.set(root, new InfiniteCollection(root));
    });
  }

  function boot() {
    initialiseWithin(document);

    var pageObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType === 1) initialiseWithin(node);
        }
      }
    });

    pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
